import { getAuthorizationToken, resetHeaderAuthorization } from "@/_services/axios"
import { resetFirebaseToken } from "@/_services/firebaseToken"
import { addNotification, addOops } from "@/_services/notification"
import { logout as oidcLogout } from "@/_services/oidc"
import { logout as samlLogout } from "@/_services/saml"
import { logout as ssoPartnerLogout } from "@/_services/ssoPartner"
import { darkReader } from "@/_services/theming"
import { resetServices } from "@/_services/utils"
import axios from "axios"

const logoutRedirectionRoute = "/authentication/login-page"

const state = {
  isAuthenticated: false,
  redirect: null,
  totpToken: null,
  totpUsername: null,
  mfaUserMethods: [], // unused for now, but there already for when we would want to add more MFA methods
  history: null,
  userLocale: null,
  termsOfServices: null,
  loginPayload: null,
}

function isAuthenticated() {
  return state?.isAuthenticated
}

function userHasLoggedOut(shouldRedirect) {
  state.shouldRedirect = shouldRedirect
  state.totpToken = null
  state.totpUsername = null
  state.isAuthenticated = false
}

function userHasLoggedIn({ history, userLocale } = {}) {
  delete state.shouldRedirect
  state.isAuthenticated = true
  if (history) state.history = history
  if (userLocale) state.userLocale = userLocale
}

function setTermsOfServices({ termsOfServices, loginPayload }) {
  state.termsOfServices = termsOfServices
  state.loginPayload = loginPayload
}

function getTermsOfServices() {
  return state.termsOfServices
}

function removeLoginPayload() {
  state.loginPayload = null
}

function recallLoginPayload() {
  const { loginPayload } = state
  state.loginPayload = null
  return loginPayload
}

function setTotpInfo({ totpToken, totpUsername, mfaUserMethods }) {
  state.totpToken = totpToken
  state.totpUsername = totpUsername
  state.mfaUserMethods = mfaUserMethods || []
}

function getTotpInfo() {
  return {
    totpToken: state.totpToken,
    totpUsername: state.totpUsername,
    mfaUserMethods: state.mfaUserMethods,
  }
}

/**
 * Watch out for the order of calls is very important.
 * - First we delete our token from the local storage and call our logout API, in case the option to invalidate tokens is active.
 * - Second we try to logout from the auth protocols.
 *   They might redirect users to an external page, in which case the function is stopped short.
 * - If no auth protocol has redirected users out, then we clean up the rest of the app internal state.
 */
async function logout({ errorMessage, callLogoutApi = true } = {}) {
  try {
    const authToken = getAuthorizationToken()
    resetHeaderAuthorization()
    resetFirebaseToken()

    // Execute this promise before the core logout because it requires an auth token
    const ssoLogoutPromise = ssoPartnerLogout({ authToken })

    // We don't await the logout API call to visually logout faster and we don't care about the response.
    // Also we explicitly reset the header authorization without waiting for the axios interceptor to do it
    // so that we can check its presence synchronously in the login page.
    if (callLogoutApi && authToken) axios.get("/api/core/auth/logout", { headers: { authorization: `Bearer ${authToken}` } })

    await ssoLogoutPromise
    await oidcLogout()
    await samlLogout()

    if (errorMessage) addNotification({ message: errorMessage, level: "error" })
    userHasLoggedOut()
    resetServices({ logout: true })
    if (state.history) {
      state.history.replace(logoutRedirectionRoute)
    }

    darkReader.disable()
  } catch (error) {
    addOops(error)
  }
}

function shouldRedirect() {
  return state.shouldRedirect
}

export {
  getTermsOfServices,
  getTotpInfo,
  isAuthenticated,
  logout,
  logoutRedirectionRoute,
  recallLoginPayload,
  removeLoginPayload,
  setTermsOfServices,
  setTotpInfo,
  shouldRedirect,
  userHasLoggedIn,
  userHasLoggedOut,
}
